/**
 * WCAG 2.1 contrast, computed rather than asserted (BUILD-836).
 *
 * PURE: no DOM, no React, no French sentence. « Ça se lit bien » is a judgment
 * arithmetic can settle, and a claim that a pairing passes AA has to have
 * counted rather than guessed.
 *
 * ## Why it survived the coupe with no importer in `src/`
 *
 * It was written for the Habillage step of the experience editor, which the
 * coupe deletes (issue #32) - and the theme of §9.4 will need it again, because
 * a closed set of settings still lets somebody pick two colours that do not
 * read. What keeps it here MEANWHILE is that it is the only place the WCAG
 * floors are written down, and `model-panel-alignment.test.ts` measures the
 * models screen against them: deleting this module would have deleted an
 * accessibility guarantee that still holds on a screen that still exists.
 *
 * ## It WARNS, it never locks
 *
 * The refusal is soft. A branding that fails AA is a real choice somebody may
 * have to make for a client, and a step that refused to save it would send them
 * to a `curl`. What a screen owes them is the number.
 *
 * ## Why the input is a hex and nothing else
 *
 * `hexColorSchema` refuses everything this file cannot read, and the two
 * decisions are one: a picker that quietly stopped measuring `oklch(...)` would
 * show an AA badge that means nothing, which is worse than showing none.
 */

/** The AA floor for normal text - the one the Habillage step judges against. */
export const AA_NORMAL_TEXT = 4.5;

/**
 * The AA floor for large text (>= 24 px, or >= 18.66 px bold).
 *
 * Exported and used deliberately sparingly: 11 px uppercase is NOT large text,
 * which is the mistake BUILD-807 and BUILD-815 both had to correct. A caller
 * asking for this floor is asserting a size, not a preference.
 */
export const AA_LARGE_TEXT = 3;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * `#rgb` or `#rrggbb` into channels, or `null` for anything else.
 *
 * `null` rather than a throw: the value comes from a form an operator is still
 * typing, and a half-typed `#10` must leave the badge silent rather than take
 * the step down.
 */
export function parseHexColor(value: string): Rgb | null {
  const match = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(value.trim());
  if (match === null) return null;
  const digits = match[1] ?? '';
  const full =
    digits.length === 3
      ? digits
          .split('')
          .map((digit) => `${digit}${digit}`)
          .join('')
      : digits;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/**
 * A colour in the ONLY form `<input type="color">` accepts: `#rrggbb`.
 *
 * It exists because the swatch is not a display, it is a CONTROL: fed the SHORT
 * form - which `hexColorSchema` legitimately accepts - the browser sanitises the
 * value to black, so the swatch renders black beside a hex field reading the
 * colour the operator chose, and the next drag commits that black over it.
 * Silently.
 *
 * (No literal colour appears in this file, comments included: the adherence
 * test refuses a hex anywhere under `src`, and it is right to - the example
 * would be a value nobody can restyle sitting next to code that computes.)
 *
 * `null` for anything unreadable, so a caller decides what to show rather than
 * being handed a plausible wrong answer.
 */
export function toSixDigitHex(value: string): string | null {
  const rgb = parseHexColor(value);
  if (rgb === null) return null;
  const pair = (channel: number): string => channel.toString(16).padStart(2, '0');
  return `#${pair(rgb.r)}${pair(rgb.g)}${pair(rgb.b)}`;
}

/** One channel, linearised - WCAG 2.1 §relative luminance, verbatim. */
function channel(value: number): number {
  const scaled = value / 255;
  return scaled <= 0.03928 ? scaled / 12.92 : ((scaled + 0.055) / 1.055) ** 2.4;
}

/** The relative luminance of a colour, 0 (black) to 1 (white). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * The contrast ratio of two colours, 1 to 21, or `null` when either is not a
 * colour this file can read.
 *
 * Symmetric by construction - the lighter of the two is found rather than
 * assumed - so a caller never has to remember which argument is the ground.
 */
export function contrastRatio(first: string, second: string): number | null {
  const a = parseHexColor(first);
  const b = parseHexColor(second);
  if (a === null || b === null) return null;
  const one = relativeLuminance(a);
  const other = relativeLuminance(b);
  const lighter = Math.max(one, other);
  const darker = Math.min(one, other);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Whether a ratio clears its floor. An unreadable pair is `false`: a badge that
 * claimed AA on a colour nobody could measure would be the one lie this file
 * exists to prevent.
 */
export function meetsContrast(ratio: number | null, floor: number = AA_NORMAL_TEXT): boolean {
  return ratio !== null && ratio >= floor;
}

/**
 * The ratio as an operator reads it: one decimal, then `:1`.
 *
 * Rounded DOWN rather than to nearest, so a 4,49 never prints « 4,5:1 » beside a
 * badge that says the pair fails - a screen that shows the floor as met and the
 * verdict as failed is a screen nobody trusts twice. French decimal comma,
 * because this string is read by an operator (M16 régime).
 */
export function formatRatio(ratio: number | null): string | null {
  if (ratio === null) return null;
  return `${(Math.floor(ratio * 10) / 10).toFixed(1).replace('.', ',')}:1`;
}
